"use client";

import * as React from "react";
import { Check, ChevronsUpDown } from "lucide-react";

import { cn } from "@/lib/utils";
import {
  Command,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * The app's one dropdown.
 *
 * It keeps the `Select` / `SelectTrigger` / `SelectValue` / `SelectContent` /
 * `SelectItem` shape the pages were written against, but is drawn by the same
 * popover and command list as `SearchableSelect`, so every dropdown in the app
 * opens, reads and highlights alike: up/down chevrons on the trigger, the tick
 * on the left of the chosen row, and a search box once the list is longer than
 * `SEARCH_THRESHOLD` — the house rule `searchable-select.tsx` explains.
 *
 * The options are read off the `SelectItem`s written inside `SelectContent`, so
 * the trigger can name the chosen one while the list is closed. They must be
 * written there directly (through arrays, fragments and conditionals is fine) —
 * an item hidden inside another component is not seen.
 */
const SEARCH_THRESHOLD = 20;

/**
 * Values that mean "nothing chosen": a filter's "All …", a form's "(none)".
 * While one is the value, the trigger reads greyed, like a placeholder.
 */
const PLACEHOLDER_VALUES = new Set(["", "all", "__none", "__inherit"]);

interface ItemInfo {
  value: string;
  node: React.ReactNode;
  text: string;
  disabled: boolean;
}

interface SelectContextValue {
  value: string | undefined;
  choose: (value: string) => void;
  disabled: boolean;
  items: ItemInfo[];
  search: string;
  setSearch: (search: string) => void;
}

const SelectContext = React.createContext<SelectContextValue | null>(null);

function useSelect(part: string) {
  const ctx = React.useContext(SelectContext);
  if (!ctx) throw new Error(`<${part}> must be used inside <Select>`);
  return ctx;
}

/** The plain text of a node, for matching what is typed in the search box. */
function textOf(node: React.ReactNode): string {
  if (node == null || typeof node === "boolean") return "";
  if (typeof node === "string" || typeof node === "number") return String(node);
  if (Array.isArray(node)) return node.map(textOf).join("");
  if (React.isValidElement<{ children?: React.ReactNode }>(node)) {
    return textOf(node.props.children);
  }
  return "";
}

/** Every `SelectItem` written anywhere under `node`. */
function collectItems(node: React.ReactNode, into: ItemInfo[] = []): ItemInfo[] {
  React.Children.forEach(node, (child) => {
    if (!React.isValidElement<SelectItemProps & { children?: React.ReactNode }>(child)) return;
    if (child.type === SelectItem) {
      into.push({
        value: child.props.value,
        node: child.props.children,
        text: textOf(child.props.children),
        disabled: Boolean(child.props.disabled),
      });
      return;
    }
    collectItems(child.props.children, into);
  });
  return into;
}

function Select({
  value: controlled,
  defaultValue,
  onValueChange,
  disabled = false,
  open: openProp,
  onOpenChange,
  children,
}: {
  value?: string;
  defaultValue?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  children?: React.ReactNode;
}) {
  const [uncontrolled, setUncontrolled] = React.useState(defaultValue);
  const value = controlled ?? uncontrolled;
  const [openState, setOpenState] = React.useState(false);
  const open = openProp ?? openState;
  const [search, setSearch] = React.useState("");

  const setOpen = (next: boolean) => {
    if (disabled && next) return;
    setOpenState(next);
    onOpenChange?.(next);
    // Reopening starts from the whole list, not inside the last search.
    if (!next) setSearch("");
  };

  const items = collectItems(children);

  const ctx: SelectContextValue = {
    value,
    disabled,
    items,
    search,
    setSearch,
    choose: (next) => {
      if (controlled === undefined) setUncontrolled(next);
      onValueChange?.(next);
      setOpen(false);
    },
  };

  return (
    <SelectContext.Provider value={ctx}>
      <Popover open={open} onOpenChange={setOpen}>
        {children}
      </Popover>
    </SelectContext.Provider>
  );
}

const SelectTrigger = React.forwardRef<
  HTMLButtonElement,
  React.ButtonHTMLAttributes<HTMLButtonElement>
>(({ className, children, disabled, ...props }, ref) => {
  const ctx = useSelect("SelectTrigger");
  const blank = ctx.value === undefined || PLACEHOLDER_VALUES.has(ctx.value);

  return (
    <PopoverTrigger asChild>
      <button
        ref={ref}
        type="button"
        role="combobox"
        disabled={disabled ?? ctx.disabled}
        data-placeholder={blank ? "" : undefined}
        className={cn(
          "flex h-9 w-full min-w-0 cursor-pointer items-center justify-between gap-2 whitespace-nowrap rounded-md border border-input bg-background px-3 py-2 text-left text-sm shadow-sm transition-colors",
          "hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
          "disabled:cursor-not-allowed disabled:opacity-50 data-[placeholder]:text-muted-foreground",
          "[&>span]:min-w-0 [&>span]:truncate",
          "aria-invalid:border-destructive aria-invalid:focus-visible:ring-destructive",
          className,
        )}
        {...props}
      >
        {children}
        <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
      </button>
    </PopoverTrigger>
  );
});
SelectTrigger.displayName = "SelectTrigger";

/**
 * What the trigger says: the chosen option's text, or the placeholder. Given
 * children, it shows those instead — for a trigger that names the choice more
 * briefly than its row in the list does.
 */
function SelectValue({
  placeholder,
  children,
  className,
}: {
  placeholder?: React.ReactNode;
  children?: React.ReactNode;
  className?: string;
}) {
  const ctx = useSelect("SelectValue");
  const chosen = ctx.items.find((item) => item.value === ctx.value);
  const shown = children ?? chosen?.node ?? placeholder;
  return (
    <span className={cn("min-w-0 truncate", className)} title={chosen?.text || undefined}>
      {shown}
    </span>
  );
}

function SelectContent({
  className,
  children,
  align = "start",
}: {
  className?: string;
  children?: React.ReactNode;
  align?: "start" | "center" | "end";
  /** Accepted for compatibility with the old Radix list; the list always drops down. */
  position?: string;
}) {
  const ctx = useSelect("SelectContent");
  const listRef = React.useRef<HTMLDivElement>(null);
  const searchable = ctx.items.length > SEARCH_THRESHOLD;
  const needle = ctx.search.trim().toLowerCase();
  const matches = needle
    ? ctx.items.filter((item) => item.text.toLowerCase().includes(needle)).length
    : ctx.items.length;

  return (
    <PopoverContent
      align={align}
      className={cn("w-[var(--radix-popover-trigger-width)] min-w-[8rem] p-0", className)}
      // Without the search box nothing in the list takes focus, and the arrow
      // keys would go nowhere; the command root is what listens for them.
      onOpenAutoFocus={(e) => {
        if (searchable) return;
        e.preventDefault();
        listRef.current?.focus();
      }}
    >
      <Command
        ref={listRef}
        tabIndex={-1}
        shouldFilter={false}
        loop
        defaultValue={ctx.value}
        className="outline-none"
      >
        {searchable && (
          <CommandInput placeholder="Search…" value={ctx.search} onValueChange={ctx.setSearch} />
        )}
        <CommandList className={cn("max-h-72", searchable && "min-h-[9rem]")}>
          <CommandGroup>{children}</CommandGroup>
          {matches === 0 && (
            <p className="px-3 py-6 text-center text-sm text-muted-foreground">No match.</p>
          )}
        </CommandList>
      </Command>
    </PopoverContent>
  );
}

interface SelectItemProps {
  value: string;
  disabled?: boolean;
  className?: string;
  children?: React.ReactNode;
}

function SelectItem({ value, disabled, className, children }: SelectItemProps) {
  const ctx = useSelect("SelectItem");
  const needle = ctx.search.trim().toLowerCase();
  if (needle && !textOf(children).toLowerCase().includes(needle)) return null;

  return (
    <CommandItem
      value={value}
      disabled={disabled}
      onSelect={() => ctx.choose(value)}
      className={className}
    >
      <Check
        className={cn("mr-2 h-4 w-4 shrink-0", ctx.value === value ? "opacity-100" : "opacity-0")}
      />
      <span className="min-w-0 flex-1 truncate">{children}</span>
    </CommandItem>
  );
}

function SelectGroup({ children }: { children?: React.ReactNode }) {
  return <>{children}</>;
}

function SelectLabel({ className, children }: { className?: string; children?: React.ReactNode }) {
  return (
    <div className={cn("px-2 py-1.5 text-xs font-medium text-muted-foreground", className)}>
      {children}
    </div>
  );
}

function SelectSeparator({ className }: { className?: string }) {
  return <CommandSeparator className={className} />;
}

export {
  Select,
  SelectGroup,
  SelectValue,
  SelectTrigger,
  SelectContent,
  SelectLabel,
  SelectItem,
  SelectSeparator,
};
